import { http } from '@lib/api';
import type { User } from '@/types/api';

// The identity list surface (`GET /api/users`) item shape (#1151). No
// `emailVerified` / `updatedAt` — the list summary does not expose them —
// and `status` is a real closed-set value (active/pending/disabled).
type RawUser = {
  id: string;
  email: string;
  name: string;
  role?: string;
  status?: string;
  createdAt?: string;
};

// The api-client unwraps the outer `{ data }` envelope, so a `GET /api/users`
// resolves to this inner page object (`platform/http` paginated envelope).
type UsersPage = {
  items: RawUser[];
  total: number;
  page: number;
  size: number;
};

// Backend `MAX_PAGE_SIZE` for the identity list (backend caps `size` here).
const PAGE_SIZE = 100;

/**
 * Fetch the full user list from the identity surface (`GET /api/users`).
 * The page renders a single non-paginated table, but the backend caps a
 * request at 100 rows (`MAX_PAGE_SIZE`), so we page through until every row
 * is collected rather than assuming one request covers all admin/operator/
 * viewer users. Throws on failure so the calling `useQuery` surfaces the
 * error through its own error channel rather than a swallowed
 * success/fallback.
 */
export async function listUsersRequest(): Promise<User[]> {
  const collected: RawUser[] = [];
  let page = 1;
  for (;;) {
    const result = await http.get<UsersPage>(`/api/users?page=${page}&size=${PAGE_SIZE}`);
    collected.push(...result.items);
    // Stop once the backend returns no rows, or once we've read the reported
    // total. Deliberately does NOT compare `result.items.length` against our
    // requested `PAGE_SIZE`: the backend's actual page size (`MAX_PAGE_SIZE`)
    // may be smaller than what we asked for, and treating a short page as
    // "last page" would silently drop remaining users if the cap shrinks.
    // The `items.length === 0` arm is the infinite-loop guard if the total
    // shifts under us between pages.
    if (result.items.length === 0 || collected.length >= result.total) break;
    page += 1;
  }
  return collected.map((u): User => ({
    id: u.id,
    email: u.email,
    name: u.name,
    role: (u.role || 'viewer') as User['role'],
    // Preserve the backend status (active/pending/disabled) verbatim — the
    // table renders non-`active` rows honestly rather than masking them.
    status: (u.status || 'active') as User['status'],
    // The list summary carries neither field; the page never displays them.
    emailVerified: false,
    createdAt: u.createdAt || new Date().toISOString(),
    updatedAt: u.createdAt || new Date().toISOString(),
  }));
}

/**
 * Mail a pending invitee a fresh invitation (#1737). The backend supersedes
 * the previously mailed link and mints a new one-time token; nothing token-
 * shaped comes back, so there is nothing here to hold on to.
 *
 * Throws on failure (409 for an account that is not pending, 404 for an
 * unknown id) so the caller's action runner surfaces it.
 */
export async function resendInvitationRequest(id: string): Promise<void> {
  await http.post(`/api/users/${id}/invitation`);
}

/**
 * Withdraw an invitation that was never accepted (#1738). The backend
 * deletes the placeholder account along with it, which is what frees the
 * email address for a fresh invite — irreversible, so every caller must put
 * a confirm in front of it.
 *
 * Throws on failure (409 for an account that is not pending, 404 for an
 * unknown id) so the caller's action runner surfaces it.
 */
export async function revokeInvitationRequest(id: string): Promise<void> {
  await http.delete(`/api/users/${id}/invitation`);
}

/**
 * Granted permission set for a single user (identity surface #943/#1109).
 * `http.get` unwraps the `{ data }` envelope, so `res.permissions` is the
 * grant list. Shared by the RoleEditor's initial open and its
 * post-role-change re-baseline so the read shape lives in one place.
 */
export async function fetchGrantedPermissions(id: string): Promise<string[]> {
  const res = await http.get<{ permissions: string[] }>(`/api/users/${id}`);
  return res.permissions || [];
}
